import {
	BookContacts20Regular,
	Call20Regular,
	Dismiss20Regular,
	Mail20Regular,
	Phone20Regular,
} from "@fluentui/react-icons";
import { CustomerInteractionHistoryProps } from "./types";
import {
	Button,
	Divider,
	MenuItem,
	Spinner,
	Text,
	tokens,
} from "@fluentui/react-components";
import { useIntl } from "react-intl";
import { useStyle } from "./style";
import { Flex, GapSize } from "@/components/atoms/flex";
import { CallTargetType, InteractionType } from "@/common/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	customerHistoryQueryKey,
	fetchCustomerHistory,
} from "@/repository/queryFns/customerHistory";
import {
	CustomerDto,
	CustomerHistoryDto,
	CustomerHistoryListItemDto,
} from "@/repository/models/customerHistory";
import { useCallback, useMemo } from "react";
import { List, ListItemProps } from "@/components/atoms/list";

import { formatDate } from "@/common/helpers/dateHelper";
import { useVoiceOutbound, useEmailOutbound } from "@/hooks/outbound";
import {
	CurrentInteractionActions,
	CurrentInteractionState,
	NewInteraction,
	NewInteractionData,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { CustomerContactMenu } from "./CustomerContactMenu";
import {
	ContactDto,
	ContactType,
	InteractionDirection,
} from "@/repository/models";
import { AddOrUpdateContactButton } from "../contacts/addOrUpdateContact/AddOrUpdateContactButton";
import { getHandlingStatusIcon } from "@/common/helpers";
import {
	InteractionComposedIcon,
	IconSize,
} from "@/components/molecules/interactionComposedIcon";
import { TargetSelectionSource } from "@/repository/models/outboundCall";
import {
	NavigationActions,
	useNavigationStore,
	ToggleTabType,
} from "@/common/stores/persistingStores/navigation";
import {
	AppCacheActions,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";
import { useResponsiveBreakpoints } from "@/hooks/responsive";

export const CustomerInteractionHistory = (
	props: CustomerInteractionHistoryProps
) => {
	const { interactionId, customerHistoryMediaType, closePopup } = props;
	const intl = useIntl();
	const classes = useStyle();
	const { isMobileMedium } = useResponsiveBreakpoints();

	const {
		isOutboundPossible,
		isOutboundAllowed,
		hasAnyOutboundRightEndpoints,
	} = useVoiceOutbound();
	const emailOutbound = useEmailOutbound();

	const queryClient = useQueryClient();

	const showNewInteraction = useCurrentInteractionStore(
		(actions: CurrentInteractionActions) => actions.showNew
	);

	const currentInteractionIsPreview = useCurrentInteractionStore(
		(state: CurrentInteractionState) => Boolean(state.preview)
	);

	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);

	const setToggledTab = useNavigationStore(
		(state: NavigationActions) => state.setToggledTab
	);

	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);

	const emailMenuItems = useCallback(
		(customer: CustomerDto) => {
			let emails = [
				{
					type: CallTargetType.Sip,
					value: customer.email,
					icon: <Mail20Regular />,
				},
			];

			return emails
				.filter((x) => {
					return !!x.value;
				})
				.map((x) => (
					<MenuItem
						key={x.value}
						icon={x.icon}
						disabled={
							!(
								emailOutbound.isOutboundAllowed &&
								emailOutbound.isOutboundPossible &&
								emailOutbound.hasAnyOutboundRightEndpoints
							)
						}
						title={x.value}
						children={x.value}
						onClick={() => {
							initOutboundEmail(customer.email, customer.name);
						}}
					/>
				));
		},
		[emailOutbound]
	);

	const initOutboundCall = (
		customerId: string,
		customerName: string,
		callTarget: string,
		type: CallTargetType
	) => {
		closePopup();

		const outboundData: NewInteractionData = {
			name: customerName,
			value: callTarget,
			selectionSource: TargetSelectionSource.CustomerHistory,
			selectionType: type,
			contactId: customerId,
		};

		var newInteraction: NewInteraction = {
			type: InteractionType.Call,
			data: outboundData,
		};

		appStoreActions.setCache(CacheKey.OutboundType, InteractionType.Call);

		setToggledTab(ToggleTabType.NewInteraction);
		showNewInteraction(newInteraction);
	};

	const initOutboundEmail = (email: string, customerName: string) => {
		closePopup();

		var newInteractionData: NewInteractionData = {
			name: customerName,
			value: email,
			selectionSource: TargetSelectionSource.CustomerHistory,
		};

		var newInteraction: NewInteraction = {
			type: InteractionType.Email,
			data: newInteractionData,
		};

		appStoreActions.setCache(CacheKey.OutboundType, InteractionType.Email);

		setToggledTab(ToggleTabType.NewInteraction);
		showNewInteraction(newInteraction);
	};

	const onContactSaved = useCallback(
		(contact: ContactDto) => {
			queryClient.setQueryData(
				[customerHistoryQueryKey, customerHistoryMediaType, interactionId],
				(oldData: CustomerHistoryDto | undefined) => {
					if (!oldData) {
						return oldData;
					}

					return {
						...oldData,
						customer: {
							...oldData.customer,
							...contact,
							mobilePhone: contact.mobilePhoneNumber,
							businessPhone: contact.businessPhoneNumber,
						},
					} as CustomerHistoryDto;
				}
			);

			props.closePopup?.();
		},
		[queryClient, props.closePopup]
	);

	const callMenuItems = useCallback(
		(customer: CustomerDto) => {
			let phoneNumbers = [
				{
					type: CallTargetType.Sip,
					value: customer.userPrincipalName,
					icon: <Call20Regular />,
				},
				{
					type: CallTargetType.Bussiness,
					value: customer.businessPhone,
					icon: <Phone20Regular />,
				},
				{
					type: CallTargetType.Mobile,
					value: customer.mobilePhone,
					icon: <BookContacts20Regular />,
				},
			];

			const mappedContactInformation = phoneNumbers
				.filter((x) => {
					return !!x.value;
				})
				.map((x) => (
					<MenuItem
						key={x.value}
						icon={x.icon}
						title={x.value}
						children={x.value}
						disabled={
							!(
								isOutboundAllowed &&
								isOutboundPossible &&
								hasAnyOutboundRightEndpoints
							)
						}
						onClick={() => {
							initOutboundCall(customer.id, customer.name, x.value, x.type);
						}}
					/>
				));
			return mappedContactInformation;
		},
		[isOutboundAllowed, isOutboundPossible, hasAnyOutboundRightEndpoints]
	);

	const {
		data: customerHistory,
		error,
		isLoading,
	} = useQuery<CustomerHistoryDto>({
		queryKey: [
			customerHistoryQueryKey,
			customerHistoryMediaType,
			interactionId,
		],
		queryFn: () =>
			fetchCustomerHistory(
				customerHistoryMediaType,
				interactionId,
				currentInteraction?.direction === InteractionDirection.Outbound &&
					currentInteraction?.isUnify
					? currentInteraction.contactInformation
					: ""
			),
	});

	const callMenu = useCallback(
		(customer: CustomerDto) => {
			let callItems = callMenuItems(customer);

			return (
				callItems.length > 0 && (
					<CustomerContactMenu
						icon={
							<Call20Regular primaryFill={tokens.colorNeutralForeground3} />
						}
						title={intl.formatMessage({ id: "Interaction.Call" })}
						menuHeader={intl.formatMessage({
							id: "CustomerHistory.CallMenuHeader",
						})}
						items={callItems}
					/>
				)
			);
		},
		[callMenuItems]
	);

	const emailMenu = useCallback(
		(customer: CustomerDto) => {
			let emailItems = emailMenuItems(customer);
			return (
				emailItems.length > 0 && (
					<CustomerContactMenu
						icon={
							<Mail20Regular primaryFill={tokens.colorNeutralForeground3} />
						}
						title={intl.formatMessage({ id: "Interaction.Email" })}
						menuHeader={intl.formatMessage({
							id: "CustomerHistory.EmailMenuHeader",
						})}
						items={emailItems}
					/>
				)
			);
		},
		[emailMenuItems]
	);

	const itemColumns = useCallback(
		(interactionHistoryItem: CustomerHistoryListItemDto) => {
			return (
				<Flex hAlign={"flex-start"} className={classes.listItem}>
					<Text
						className={classes.listItemTime}
						size={200}
						title={formatDate(
							interactionHistoryItem.arrivalTime,
							true,
							false,
							true,
							false
						)}
					>
						{formatDate(
							interactionHistoryItem.arrivalTime,
							true,
							false,
							true,
							false
						)}
					</Text>
					<Text
						title={
							interactionHistoryItem.isHandled
								? intl.formatMessage({ id: "History.Handled" })
								: intl.formatMessage({ id: "History.Unhandled" })
						}
						className={classes.listItemHandledIcon}
					>
						{getHandlingStatusIcon(interactionHistoryItem.isHandled)}
					</Text>
					<Text
						size={200}
						className={classes.agentName}
						title={interactionHistoryItem.agentName}
					>
						{interactionHistoryItem.agentName}
					</Text>
					<Text size={200} title={interactionHistoryItem.queueName}>
						{interactionHistoryItem.queueName}
					</Text>
				</Flex>
			);
		},
		[]
	);

	const customerHistoryItems = useMemo<ListItemProps[]>(
		() =>
			customerHistory?.interactionHistory.map(
				(customerHistoryItemDto: CustomerHistoryListItemDto) => ({
					key: customerHistoryItemDto.id,
					media: (
						<InteractionComposedIcon
							size={IconSize.Medium}
							interactionType={customerHistoryItemDto.type}
							isMissed={customerHistoryItemDto.isMissed}
							isInbound={customerHistoryItemDto.isInboundInteraction}
						/>
					),
					header: itemColumns(customerHistoryItemDto),
					content: customerHistoryItemDto.subject,
				})
			) ?? [],
		[customerHistory]
	);

	return (
		<div className={classes.container}>
			{isLoading && (
				<Flex column vAlign="center">
					<Spinner
						size="tiny"
						labelPosition="above"
						label={intl.formatMessage({ id: "Common.Loading" })}
					/>
				</Flex>
			)}
			{error && (
				<Flex
					hAlign={"flex-start"}
					vAlign={"center"}
					className={classes.messageContainer}
				>
					{intl.formatMessage({ id: "CustomerHistory.ErrorOnFetchingTheData" })}
				</Flex>
			)}
			{customerHistory && (
				<>
					<Flex
						hAlign={"space-between"}
						vAlign={"flex-start"}
						className={
							!customerHistory.customer?.id
								? classes.popoverHeaderBiggerMargin
								: classes.popoverHeaderLowerMargin
						}
					>
						<>
							<div>
								{customerHistory && (
									<>
										{!customerHistory.customer?.id &&
											!isMobileMedium &&
											!currentInteractionIsPreview && (
												<AddOrUpdateContactButton
													identifiedContact={customerHistory.customer!}
													interactionId={interactionId}
													interactionType={customerHistoryMediaType}
													onContactSaved={onContactSaved}
												/>
											)}
										{customerHistory.customer?.id && (
											<Flex column gap={GapSize.Large}>
												{customerHistory.customer.contactType !=
													ContactType.AzureActiveDirectory &&
													!isMobileMedium &&
													!currentInteractionIsPreview && (
														<AddOrUpdateContactButton
															identifiedContact={customerHistory.customer!}
															interactionId={interactionId}
															interactionType={customerHistoryMediaType}
															onContactSaved={onContactSaved}
														/>
													)}
												<Flex gap={GapSize.Large}>
													{callMenu(customerHistory.customer)}
													{emailMenu(customerHistory.customer)}
												</Flex>
											</Flex>
										)}
									</>
								)}
							</div>
							<Button
								appearance="transparent"
								icon={<Dismiss20Regular />}
								onClick={closePopup}
								className={classes.closeButton}
							/>
						</>
					</Flex>
					{customerHistory?.customer?.id &&
						(customerHistory?.customer.jobTitle ||
							customerHistory?.customer.department) && (
							<Flex>
								<Text weight="semibold" className={classes.customerInfo}>
									{[
										customerHistory?.customer.jobTitle,
										customerHistory?.customer.department,
									]
										.filter((x) => !!x)
										.join(" / ")}
								</Text>
							</Flex>
						)}
					{customerHistoryItems.length > 0 && (
						<>
							<Flex>
								<Text size={200} className={classes.listTitle}>
									{intl.formatMessage({ id: "CustomerHistory.ListHeader" })}
								</Text>
							</Flex>
							<Divider />
							<List
								classesName={{
									item: classes.listItem,
									startMedia: classes.listItemIcon,
									endMedia: classes.listItemEndMedia,
								}}
								items={customerHistoryItems}
							/>
						</>
					)}
					{customerHistoryItems.length == 0 && (
						<Flex
							hAlign={"flex-start"}
							vAlign={"center"}
							className={classes.messageContainer}
						>
							{intl.formatMessage({ id: "CustomerHistory.NoItemsMessage" })}
						</Flex>
					)}
				</>
			)}
		</div>
	);
};
